<template>
  <div>
    <b-row
      class="d-flex flex-column w-25 mb-8 mx-0"
    >
      <div
        class="top-filters"
      >
        <p>States</p>
        <base-filter-dropdown
          class="w-100 custom-height-dropdown"
          :filter="'States'"
          :options-list="filterSelections.states.options"
          :key-property="'id'"
          :value-property="'value'"
          :selected-options="selectedFilters.states"
          :disabled="areFiltersLocked"
          @updatedSelectedOptions="selectFilter('states', ...arguments)"
          @selectAllOptions="selectAllOptions('states')"
          @deselectAllOptions="deselectAllOptions('states')"
        />
      </div>
      <div
        class="top-filters"
      >
        <p>Date Range</p>
        <date-picker-dropdown
          @updateDates="updateDates"
          left
          toggle-class="text-left"
          default-dates="this month"
        />
      </div>
    </b-row>
    <p>Report Filters</p>
    <b-row
      class="d-flex w-100 mx-0"
    >
      <div
        class="d-flex w-75 align-items-end"
      >
        <div
          class="report-filters"
        >
          <div
            class="d-flex align-items-center"
          >
            <div
              class="spinner-border spinner-border-sm ml-auto"
              :class="{'invisible' : !isFilterLoading('advertiser')}"
              role="status"
            >
              <span class="invisible" />
            </div>
          </div>
          <b-form-select
            :value="selectedFilters.advertiserId"
            @change="selectFilter('advertiserId', $event)"
            size="sm"
            :disabled="isFilterDisabled('advertiser') || areFiltersLocked"
          >
            <b-form-select-option
              :value="null"
              disabled
            >
              Select Advertiser
            </b-form-select-option>
            <b-form-select-option
              v-for="(option, index) in getFilterOptions('advertiser')"
              :key="index"
              :value="option.id"
            >
              {{ option.name }}
            </b-form-select-option>
          </b-form-select>
        </div>
        <div
          class="report-filters"
        >
          <div
            class="d-flex align-items-center"
          >
            <div
              class="spinner-border spinner-border-sm ml-auto"
              :class="{'invisible' : !isFilterLoading('campaign')}"
              role="status"
            >
              <span class="invisible" />
            </div>
          </div>
          <b-form-select
            :value="selectedFilters.campaignId"
            @change="selectFilter('campaignId', $event)"
            size="sm"
            :disabled="isFilterDisabled('campaign')"
          >
            <b-form-select-option
              :value="null"
              disabled
            >
              Select Campaign
            </b-form-select-option>
            <b-form-select-option
              v-for="(option, index) in getFilterOptions('campaign')"
              :key="index"
              :value="option.id"
            >
              {{ option.name }}
            </b-form-select-option>
          </b-form-select>
        </div>
        <div
          class="report-filters"
        >
          <div
            class="d-flex align-items-center"
          >
            <div
              class="spinner-border spinner-border-sm ml-auto"
              :class="{'invisible' : !isFilterLoading('publishers')}"
              role="status"
            >
              <span class="invisible" />
            </div>
          </div>
          <base-filter-dropdown
            class="w-100 custom-height-dropdown"
            :filter="'publishers'"
            :options-list="filterSelections.publishers.options"
            :key-property="'id'"
            :value-property="'name'"
            :selected-options="selectedFilters.publishers"
            @updatedSelectedOptions="updatedSelectedOptions('publishers', ...arguments)"
            @selectAllOptions="selectAllOptions('publishers')"
            @deselectAllOptions="deselectAllOptions('publishers')"
            :disabled="isFilterDisabled('publishers')"
          />
        </div>
        <div
          class="report-filters"
        >
          <div
            class="d-flex align-items-center"
          >
            <div
              class="spinner-border spinner-border-sm ml-auto"
              :class="{'invisible' : !isFilterLoading('creatives')}"
              role="status"
            >
              <span class="invisible" />
            </div>
          </div>
          <base-filter-dropdown
            class="w-100 custom-height-dropdown"
            :filter="'creatives'"
            :options-list="filterSelections.creatives.options"
            :key-property="'id'"
            :value-property="'name'"
            :selected-options="selectedFilters.creatives"
            @updatedSelectedOptions="updatedSelectedOptions('creatives', ...arguments)"
            @selectAllOptions="selectAllOptions('creatives')"
            @deselectAllOptions="deselectAllOptions('creatives')"
            :disabled="isFilterDisabled('creatives')"
          />
        </div>
      </div>
      <b-button
        variant="primary"
        size="md"
        class="align-self-end ml-auto"
        :class="{ 'btn-loading': isAddReportLoading }"
        :disabled="isAddReportDisabled"
        @click="addReport()"
      >
        Add Report
      </b-button>
    </b-row>
  </div>
</template>
<script>
/* eslint-disable no-fallthrough */
import { mapActions, mapGetters } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';
import * as reportGeneratorsTypes from '@/modules/ReportsGenerator/store/types';
import helper from '@/core/helper';
import _ from 'lodash';

import DatePickerDropdown from '@/components/DatePickerDropdown.vue';
import BaseFilterDropdown from '@/components/BaseFilterDropdown.vue';
import * as accountTypes from '@/modules/account/store/types';

export default {
  name: 'ReportsGeneratorFilters',
  components: {
    DatePickerDropdown,
    BaseFilterDropdown,
  },
  data() {
    return {
      selectedFilters: {
        states: [],
        advertiserId: null,
        campaignId: null,
        publishers: [],
        creatives: [],
      },
      filterSelections: {
        states: {
          isLoading: false,
          options: [
            { id: 'DORMANT', value: 'Dormant' },
            { id: 'LIVE', value: 'Live' },
            { id: 'PAUSED', value: 'Paused' },
            { id: 'ENDED', value: 'Ended' },
          ],
        },
        advertiser: {
          isLoading: false,
          options: [],
        },
        campaign: {
          isLoading: false,
          options: [],
        },
        publishers: {
          isLoading: false,
          options: [],
        },
        creatives: {
          isLoading: false,
          options: [],
        },
      },
      ownedAdvertisers: [],
      dateRange: {
        start: -1,
        end: -1,
      },
      areFiltersLocked: false,
      isFiltersValuesChanged: false,
      isAddReportLoading: false,
    };
  },
  computed: {
    ...mapGetters('account', [
      accountTypes.GET_IS_MANAGED_ACCOUNT,
    ]),
    isAddReportDisabled() {
      return Object.values(this.selectedFilters)
        .some((filterOptions) => (Array.isArray(filterOptions) ? !filterOptions.length : !filterOptions))
        || this.dateRange.start === -1 || this.dateRange.end === -1
        || !this.isFiltersValuesChanged;
    },
  },
  methods: {
    ...mapActions('account', [
      accountTypes.FETCH_ACCOUNT,
    ]),
    ...mapActions('campaigns', [
      campaignTypes.FETCH_CAMPAIGNS,
      campaignTypes.FETCH_PUBLISHERS,
    ]),
    ...mapActions('content', [
      contentTypes.FETCH_EXISTING_CREATIVES_AS_SHAREE,
    ]),
    ...mapActions('reportsGenerator', [
      reportGeneratorsTypes.FETCH_REPORTS_ADVERTISERS,
      reportGeneratorsTypes.FETCH_REPORTS_SHARED_ADVERTISERS,
      reportGeneratorsTypes.FETCH_REPORTS_SHARED_CAMPAIGNS,
      reportGeneratorsTypes.SET_SELECTED_FILTER,
      reportGeneratorsTypes.SET_SELECTED_ADVERTISER_DETAILS,
      reportGeneratorsTypes.FETCH_REPORTS_SELF_SERVICE_CAMPAIGNS,
      reportGeneratorsTypes.FETCH_PUBLISHER_IDS_WITH_STATS,
    ]),
    updatedSelectedOptions(filter, id, isChecked) {
      this.isFiltersValuesChanged = true;
      if (isChecked) return this.selectedFilters[filter].push(id);
      const index = this.selectedFilters[filter].indexOf(id);
      this.selectedFilters[filter].splice(index, 1);
    },
    selectAllOptions(filter) {
      this.selectedFilters[filter] = this.filterSelections[filter]?.options.map((option) => option.id);
      this.isFiltersValuesChanged = true;
      if (filter === 'states') {
        this.resetFilters(filter);
        this.fetchAdvertisers();
      }

      if (filter === 'creatives') {
        this.resetFilters(filter);
        this.fetchPublishers();
      }
    },
    deselectAllOptions(filter) {
      this.selectedFilters[filter] = [];
    },
    areAllOptionsSelected(filter) {
      return this.selectedFilters[filter].length === this.filterSelections[filter].options.length;
    },
    isFilterDisabled(filter) {
      if (this.dateRange.start === -1 || this.dateRange.end === -1) return true;
      switch (filter) {
        case 'advertiser': {
          return !this.selectedFilters.states
          || !this.getFilterOptions('advertiser').length;
        }
        case 'campaign': {
          return !this.selectedFilters.advertiserId
          || !this.getFilterOptions('campaign').length;
        }
        case 'publishers': {
          return !this.selectedFilters.campaignId
          || !this.getFilterOptions('publishers').length;
        }
        case 'creatives': {
          return !this.selectedFilters.campaignId
          || !this.getFilterOptions('creatives').length;
        }
        default: return true;
      }
    },
    selectFilter(filter, value, isChecked) {
      switch (filter) {
        case 'states':
          this.updatedSelectedOptions(filter, value, isChecked);
          this.resetFilters('states');
          this.fetchAdvertisers();
          break;
        case 'advertiserId':
          this.updateFilter(filter, value);
          this.resetFilters('advertiserId');
          this.SET_SELECTED_ADVERTISER_DETAILS(this.getFilterDetails('advertiser', value));
          this.fetchCampaigns();
          break;
        case 'campaignId':
          this.updateFilter(filter, value);
          this.resetFilters('campaignId');
          this.fetchCreatives();
          this.fetchPublishers();
          break;
        default:
          this.updateFilter(filter, value);
          break;
      }
      this.isFiltersValuesChanged = true;
    },
    updateFilter(filter, value) {
      this.selectedFilters[filter] = value;
      this.SET_SELECTED_FILTER({ filter, value });
    },
    resetFilters(updatedFilter) {
      switch (updatedFilter) {
        case 'states': {
          this.selectedFilters.advertiserId = null;
          this.filterSelections.advertiser.options = [];
        }
        case 'advertiserId': {
          this.selectedFilters.campaignId = null;
          this.filterSelections.campaign.options = [];
        }
        case 'campaignId': {
          this.selectedFilters.publishers = [];
          this.filterSelections.publishers.options = [];
          this.selectedFilters.creatives = [];
          this.filterSelections.creatives.options = [];
        }
        default:
          break;
      }
    },
    updateDates(startDate, endDate) {
      this.dateRange.start = startDate;
      this.SET_SELECTED_FILTER({
        filter: 'startDate',
        value: startDate,
      });
      this.dateRange.end = endDate;
      this.SET_SELECTED_FILTER({
        filter: 'endDate',
        value: endDate,
      });
    },
    fetchAdvertisers() {
      this.filterSelections.advertiser.isLoading = true;
      const fetchAdvertisersPromise = this.GET_IS_MANAGED_ACCOUNT ? this.FETCH_REPORTS_ADVERTISERS : this.FETCH_REPORTS_SHARED_ADVERTISERS;
      fetchAdvertisersPromise({ states: this.selectedFilters.states })
        .then((advertisers) => {
          const ownedAndSharedAdvertisers = advertisers.concat(this.ownedAdvertisers);
          this.filterSelections.advertiser.options = helper.orderBy(ownedAndSharedAdvertisers, 'name');
        })
        .finally(() => {
          this.filterSelections.advertiser.isLoading = false;
        });
    },
    fetchCampaigns() {
      this.filterSelections.campaign.isLoading = true;
      const filters = {
        states: this.selectedFilters.states,
        advertiserId: this.selectedFilters.advertiserId,
      };

      let fetchCampaignPromise;

      if (this.GET_IS_MANAGED_ACCOUNT) {
        fetchCampaignPromise = this.FETCH_CAMPAIGNS;
      } else {
        const advertiser = this.ownedAdvertisers.find((ownedAdvertiser) => ownedAdvertiser.id === this.selectedFilters.advertiserId);
        fetchCampaignPromise = advertiser ? this.FETCH_REPORTS_SELF_SERVICE_CAMPAIGNS : this.FETCH_REPORTS_SHARED_CAMPAIGNS;
      }

      fetchCampaignPromise({ filters })
        .then((campaigns) => {
          this.filterSelections.campaign.options = campaigns;
        })
        .finally(() => {
          this.filterSelections.campaign.isLoading = false;
        });
    },
    fetchPublishers() {
      this.filterSelections.publishers.isLoading = true;

      if (this.getFilterOptions('creatives').length !== 0) {
        const creativeIds = this.getFilterOptions('creatives').map((creative) => creative.id);
        Promise.all([this.FETCH_PUBLISHERS(), this.FETCH_PUBLISHER_IDS_WITH_STATS({ creativeIds })])
          .then(([publishers, publisherIdsWithStats]) => {
            this.filterSelections.publishers.options = publishers.filter((publisher) => publisherIdsWithStats.includes(publisher.id));
            this.selectAllOptions('publishers');
          })
          .finally(() => {
            this.filterSelections.publishers.isLoading = false;
          });
      }
    },
    fetchCreatives() {
      this.filterSelections.creatives.isLoading = true;
      const { campaignId } = this.selectedFilters;
      this.FETCH_EXISTING_CREATIVES_AS_SHAREE([campaignId])
        .then(({ creatives }) => {
          this.filterSelections.creatives.options = creatives;
          this.selectAllOptions('creatives');
        })
        .finally(() => {
          this.filterSelections.creatives.isLoading = false;
        });
    },
    getFilterOptions(filter) {
      return this.filterSelections[filter].options;
    },
    isFilterLoading(filter) {
      return this.filterSelections[filter].isLoading;
    },
    getFilterDetails(filter, id) {
      return this.getFilterOptions(filter).find((option) => option.id === id);
    },
    addReport() {
      this.isAddReportLoading = true;
      const getFilterEmit = (filter) => {
        if (this.areAllOptionsSelected(filter)) {
          return {
            name: 'All',
            ids: this.selectedFilters[filter],
          };
        }
        if (this.selectedFilters[filter].length === 1) {
          return this.getFilterDetails(filter, this.selectedFilters[filter][0]);
        }
        return {
          name: '',
          ids: this.selectedFilters[filter],
        };
      };

      this.$emit('addReport', {
        campaign: this.getFilterDetails('campaign', this.selectedFilters.campaignId),
        publisher: _.cloneDeep(getFilterEmit('publishers')),
        creative: _.cloneDeep(getFilterEmit('creatives')),
      });
      this.isFiltersValuesChanged = false;
      this.isAddReportLoading = false;
      this.areFiltersLocked = true;
    },
  },
  mounted() {
    this.FETCH_ACCOUNT().then((response) => {
      this.ownedAdvertisers = response.advertisers;
    });
  },
};
</script>
<style scoped lang="scss">
p {
  margin-bottom: 0px;
}
.top-filters {
  min-width: 260px;
  margin-bottom: 1rem;
}
.report-filters {
  flex-grow: 1;
  flex-basis: calc((100% / 4) - 1rem);
}
.report-filters:not(:last-child) {
  margin-right: 1rem;
}
::v-deep {
  .custom-select > option:first-child {
    display: none;
  }
  .spinner-border {
    margin-right:2px;
    color: $blue;
  }
  .custom-height-dropdown {
    height: calc(1.5em + 0.5rem + 2px);
  }
}
</style>
